import React, { useContext } from 'react'
import { t } from '@lingui/macro'

import { ManageCampaignContext } from '../ManageCampaignPage'
import { DownloadCSVButton } from '../../../components/common/download-csv-button/DownloadCSVButton'
import {
  CampaignTrackedLinkReportsDocument,
  CampaignTrackedLinkReportsQuery
} from './CampaignLinkReportOverviewTable.codegen'
import { ICampaignLinkReportsFilters } from './CampaignLinkReport'
import { CampaignUserLinkClicksReportRow } from '../../../graphql/public/PublicSchemaTypes'
import {
  ISelectedActions,
  protocolRegexp,
  trailingSlashRegexp
} from './CampaignLinkReportOverviewTable'

interface ICampaignReportDownloadCSVProps {
  itemsCount: number
  filters: ICampaignLinkReportsFilters
  trackedLinks: NonNullable<
    CampaignTrackedLinkReportsQuery['admin']
  >['trackedLinks']
  selectedActions: ISelectedActions
}

enum LinkReportColumn {
  FIRST_CLICK = 'FIRST_CLICK',
  LAST_CLICK = 'LAST_CLICK',
  CLICK_COUNT = 'CLICK_COUNT'
}

interface ICsvHeader {
  actionName: string
  trackedLinkId: number
  reportType: LinkReportColumn
}

const CampaignLinkReportDownloadCSV = ({
  itemsCount,
  filters,
  trackedLinks,
  selectedActions
}: ICampaignReportDownloadCSVProps) => {
  const { campaign } = useContext(ManageCampaignContext)
  const csvColumns = buildCsvHeaderColumns(trackedLinks)
  return (
    <DownloadCSVButton
      count={itemsCount}
      fileName={`looop-campaign-link-report-${campaign.id}`}
      dataPath={'data.admin.campaign.trackedLinksReport.users'}
      queryOptions={(page, limit) => ({
        variables: {
          campaignId: campaign.id,
          trackedLinkReportFilters: {
            displayName: filters.search.length > 0 ? filters.search : null,
            page: page - 1,
            limit: limit,
            ...selectedActions
          },
          trackedLinksFilters: selectedActions
        },
        query: CampaignTrackedLinkReportsDocument,
        fetchPolicy: 'no-cache'
      })}
      prepareData={(data) => {
        return data.map((row: CampaignUserLinkClicksReportRow) => ({
          ...mapUserDataToCsvColumns(row, csvColumns)
        }))
      }}
    />
  )
}

const buildCsvHeaderColumns = (trackedLinks) => {
  const tableColumns: Array<ICsvHeader> = []
  let index = 0

  trackedLinks.forEach((trackedLink) => {
    const link = (trackedLink.link as string)
      .replace(protocolRegexp, '')
      .replace(trailingSlashRegexp, '')

    tableColumns.push(
      {
        trackedLinkId: trackedLink.id as number,
        actionName: t`${++index}. First click (${link})`,
        reportType: LinkReportColumn.FIRST_CLICK
      },
      {
        trackedLinkId: trackedLink.id as number,
        actionName: t`${++index}. Last click (${link})`,
        reportType: LinkReportColumn.LAST_CLICK
      },
      {
        trackedLinkId: trackedLink.id as number,
        actionName: t`${++index}. Click count (${link})`,
        reportType: LinkReportColumn.CLICK_COUNT
      }
    )
  })

  return tableColumns
}

const mapUserDataToCsvColumns = (
  userReportRow: CampaignUserLinkClicksReportRow,
  csvColumns: Array<ICsvHeader>
) => {
  const singleCsvRow: Record<string, any> = {
    [t`Username`]: userReportRow.user?.display_name,
    [t`User ID`]: userReportRow.user?.id
  }

  // Find value for every CSV header. If value is not found, empty string is passed.
  csvColumns.forEach((csvColumn) => {
    let value = ''

    if (Array.isArray(userReportRow.linkClicks)) {
      const trackedLinkReport = userReportRow.linkClicks.find(
        (UserLinkClick) =>
          UserLinkClick.tracked_link_id === csvColumn.trackedLinkId
      )

      if (trackedLinkReport) {
        switch (csvColumn.reportType) {
          case LinkReportColumn.CLICK_COUNT:
            value = `${trackedLinkReport.total_clicks_count}`
            break
          case LinkReportColumn.FIRST_CLICK:
            value = trackedLinkReport.first_click_at as string
            break
          case LinkReportColumn.LAST_CLICK:
            value = trackedLinkReport.last_click_at as string
            break
        }
      }
    }

    singleCsvRow[csvColumn.actionName] = value
  })
  return singleCsvRow
}

export default CampaignLinkReportDownloadCSV
